import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import logo from "@/assets/logo.webp";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Über uns", href: "/ueber-uns" },
  { name: "Produkte & Service", href: "/produkte-service" },
  { name: "Regionales", href: "/regionales" },
  { name: "Aktuelles", href: "/aktuelles" },
  { name: "Kontakt", href: "/kontakt" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-secondary border-b border-border">
        <div className="container-custom py-2">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <div className="flex items-center gap-6">
              <a 
                href="tel:+496145321455" 
                className="flex items-center gap-2 hover:text-primary transition-colors"
                aria-label="Telefon: 06145 321455"
              >
                <Phone className="w-4 h-4" />
                <span>06145 321455</span>
              </a>
              <a 
                href="mailto:vigheshan.gahndi@rewe-kaufleute.com" 
                className="flex items-center gap-2 hover:text-primary transition-colors"
                aria-label="E-Mail senden"
              >
                <Mail className="w-4 h-4" />
                <span>vigheshan.gahndi@rewe-kaufleute.com</span>
              </a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Industriestraße 40, 65439 Flörsheim-Weilbach</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-border"
            : "bg-background"
        }`}
      >
        <nav className="container-custom" aria-label="Hauptnavigation">
          <div className="flex items-center justify-between h-24 lg:h-32">
            {/* Placeholder for centering on mobile */}
            <div className="lg:hidden w-10" />

            {/* Logo - centered on mobile */}
            <Link to="/" className="flex-shrink-0 lg:flex-shrink" aria-label="REWE V. Gahndi - Zur Startseite">
              <motion.img
                src={logo}
                alt="REWE V. Gahndi Logo"
                width={700}
                height={307}
                className="h-16 lg:h-24 w-auto"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className={`px-3 py-2 text-xs xl:text-sm font-medium uppercase tracking-normal xl:tracking-wide whitespace-nowrap transition-colors duration-200 rounded-lg ${
                      location.pathname === item.href
                        ? "text-primary bg-primary/10"
                        : "text-foreground hover:text-primary hover:bg-primary/5"
                    }`}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg text-foreground hover:text-primary hover:bg-primary/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden border-t border-border bg-background"
            >
              <div className="container-custom py-4 space-y-1">
                {navigation.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      className={`block px-4 py-3 text-center text-base font-medium uppercase tracking-wide rounded-lg transition-colors ${
                        location.pathname === item.href
                          ? "text-primary bg-primary/10"
                          : "text-foreground hover:text-primary hover:bg-primary/5"
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile contact info */}
                <div className="pt-4 mt-4 border-t border-border space-y-3 text-center text-sm text-muted-foreground">
                  <a 
                    href="tel:+496145321455" 
                    className="flex items-center justify-center gap-2 hover:text-primary transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>06145 321455</span>
                  </a>
                  <p className="flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>Flörsheim-Weilbach</span>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
